
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useThemeMode } from '@/hooks/useThemeMode';
import { useThemeContext } from '@/hooks/useThemeContext';
import { useBrochurePDFExport } from '@/hooks/useBrochurePDFExport';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import CoverPage from './pages/CoverPage';
import MeetTheFounderPage from './pages/MeetTheFounderPage';
import ProblemSolutionPage from './pages/ProblemSolutionPage';
import ToolEcosystemPage from './pages/ToolEcosystemPage';
import SmartBuyPage from './pages/SmartBuyPage';
import OfferScopePage from './pages/OfferScopePage';
import SuccessStoriesPage from './pages/SuccessStoriesPage';
import JourneyFlowPage from './pages/JourneyFlowPage';
import ComparisonPage from './pages/ComparisonPage';
import TechnologyPage from './pages/TechnologyPage';
import BrochureFooter from './components/BrochureFooter';
import { BrochurePage } from './components/BrochurePage';

const EquiForgeBrochure: React.FC = () => {
  const { resolvedTheme } = useThemeMode();
  const { glassMorphism } = useThemeContext();
  const brochureRef = useRef<HTMLDivElement>(null);
  const { exportBrochureAsPDF, isExporting } = useBrochurePDFExport();
  
  // Handle prefers-reduced-motion
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  const handleExportPDF = () => {
    exportBrochureAsPDF(brochureRef, 'equiforge-brochure.pdf');
  };
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <LayoutGroup>
      <div className="flex flex-col w-full">
        {/* Export PDF Button - Fixed Position */}
        <div className="fixed top-20 right-6 z-50">
          <Button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="gap-2 shadow-lg"
            size="lg"
          >
            <Download className="w-4 h-4" />
            {isExporting ? 'Generating PDF...' : 'Export as PDF'}
          </Button>
        </div>

        <AnimatePresence>
          <motion.div 
            ref={brochureRef}
            className="w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="w-full max-w-7xl mx-auto">
              {/* Cover Page */}
              <BrochurePage>
                <CoverPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Meet the Founder Page */}
              <BrochurePage>
                <MeetTheFounderPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Problem Solution Page */}
              <BrochurePage>
                <ProblemSolutionPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Tool Ecosystem Page */}
              <BrochurePage>
                <ToolEcosystemPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Core Analysis Tools */}
              <BrochurePage>
                <SmartBuyPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Investment Intelligence Suite */}
              <BrochurePage>
                <OfferScopePage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Success Stories & Stats */}
              <BrochurePage>
                <SuccessStoriesPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Journey Flow Page */}
              <BrochurePage>
                <JourneyFlowPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Comparison Page */}
              <BrochurePage>
                <ComparisonPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Technology & Security */}
              <BrochurePage>
                <TechnologyPage reducedMotion={prefersReducedMotion} />
              </BrochurePage>
              
              {/* Footer */}
              <BrochureFooter />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </LayoutGroup>
  );
};

export default EquiForgeBrochure;
